import { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function SecurityQuestionCard() {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(null); // {has_question, question_id}
  const [qid, setQid] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [qResp, meResp] = await Promise.all([
        api.get("/security/questions"),
        api.get("/security/me"),
      ]);
      setQuestions(qResp.data.questions || []);
      setCurrent(meResp.data);
      if (meResp.data?.question_id) setQid(meResp.data.question_id);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!qid || answer.trim().length < 2) return;
    setSaving(true);
    try {
      await api.post("/security/me", { question_id: qid, answer: answer.trim() });
      toast.success("Question de sécurité enregistrée ✅");
      setSuccess(true);
      setAnswer("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer votre question de sécurité ? Vous ne pourrez plus récupérer votre mot de passe sans elle.")) return;
    setSaving(true);
    try {
      await api.delete("/security/me");
      toast.success("Question de sécurité supprimée");
      setQid("");
      setAnswer("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2.5 text-sm rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-[#E11D48] focus:outline-none";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8 shadow-sm" data-testid="security-question-card">
      <div className="flex items-start justify-between mb-5 gap-3">
        <div>
          <div className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold uppercase flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> // PASSWORD RECOVERY
          </div>
          <h2 className="font-heading font-bold text-xl tracking-tight mt-1 text-slate-900">
            Question secrète
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Choisissez une question pour pouvoir réinitialiser votre mot de passe en cas d'oubli (sans e-mail).
          </p>
        </div>
        {current?.has_question && (
          <button
            onClick={remove}
            disabled={saving}
            data-testid="security-remove-btn"
            className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition shrink-0"
            title="Supprimer la question"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {current?.has_question && (
        <div className="mb-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-start gap-2" data-testid="security-status-set">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Vous avez une question secrète enregistrée :{" "}
            <strong>
              {(questions.find((q) => q.id === current.question_id)?.fr) || current.question_id}
            </strong>
            <div className="text-emerald-600/70 mt-1">
              Vous pouvez la modifier ci-dessous en choisissant une nouvelle question et une nouvelle réponse.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
            Choisissez une question
          </label>
          <select
            value={qid}
            onChange={(e) => setQid(e.target.value)}
            disabled={loading}
            data-testid="security-question-select"
            required
            className={`${inputCls} bg-white`}
          >
            <option value="" disabled>— Sélectionner —</option>
            {questions.map((q) => (
              <option key={q.id} value={q.id}>{q.fr}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
            Votre réponse
          </label>
          <input
            type="text"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Réponse (insensible aux majuscules)"
            data-testid="security-answer-input"
            required
            minLength={2}
            maxLength={120}
            autoComplete="off"
            className={inputCls}
          />
          <p className="text-[10px] text-slate-400 mt-1">
            Conseil : choisissez une réponse simple et mémorisable. La comparaison est insensible à la casse et aux espaces.
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Question secrète enregistrée. Conservez-la précieusement.</span>
          </div>
        )}

        <div className="pt-1">
          <button
            type="submit"
            disabled={!qid || answer.trim().length < 2 || saving}
            data-testid="security-save-btn"
            className="btn-primary text-xs uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Enregistrement…" : current?.has_question ? "Mettre à jour" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
